/**
 * help/types — the content model for in-app help.
 *
 * WHY TYPED BLOCKS AND NOT MARKDOWN: the app ships no markdown renderer and we
 * are not adding a dependency to render prose. Blocks give us three things a
 * markdown string would not: a renderer that can't produce off-brand markup, a
 * search indexer that knows which text is a heading vs a step, and a compile-time
 * error when an article references a screenshot field that doesn't exist.
 *
 * WRITING RULE (enforced in part by help/registry.test.ts):
 * every article is written for somebody who has never seen a property-tax system.
 * Short sentences. Say "you". Define a word the first time it appears. If a
 * sentence needs a comma to survive, it probably needs to be two sentences.
 */

/** The categories the Help Center groups articles under, in display order. */
export const HELP_CATEGORIES = [
  { id: 'start', label: 'Start here', blurb: 'What this app is and how to find your way around.' },
  { id: 'basics', label: 'How the pieces fit', blurb: 'Clients, properties, parcels, appeals — what each word means.' },
  { id: 'setup', label: 'Setting up your book', blurb: 'Adding clients, properties and parcels, or importing them.' },
  { id: 'appeals', label: 'Working appeals', blurb: 'Filing, deadlines, hearings and moving work forward.' },
  { id: 'values', label: 'Values and savings', blurb: 'What a property is worth, and how much you saved the client.' },
  { id: 'taxbills', label: 'Tax bills and returns', blurb: 'Bills coming in, and returns going out.' },
  { id: 'billing', label: 'Getting paid', blurb: 'Fee agreements, invoices and splits.' },
  { id: 'docs', label: 'Documents and requests', blurb: 'Files, review queues and asking clients for things.' },
  { id: 'people', label: 'People and access', blurb: 'Contacts, teammates, roles and the client portal.' },
  { id: 'reports', label: 'Reports', blurb: 'Getting answers out of your data.' },
  { id: 'admin', label: 'Settings and admin', blurb: 'Org-wide setup that usually only an admin touches.' },
] as const;

export type HelpCategoryId = (typeof HELP_CATEGORIES)[number]['id'];

/** One renderable piece of an article. */
export type HelpBlock =
  /** A plain paragraph. */
  | { kind: 'text'; text: string }
  /** A subheading inside an article. */
  | { kind: 'heading'; text: string }
  /** A numbered how-to. `intro` is optional lead-in prose. */
  | { kind: 'steps'; intro?: string; steps: string[] }
  /** A bulleted list where order does not matter. */
  | { kind: 'bullets'; intro?: string; items: string[] }
  /**
   * A screenshot. `src` is a path under /help-shots/ (served from web/public).
   * `alt` is REQUIRED and must describe what the picture shows, because a
   * screen reader user gets nothing else — "screenshot" is not an alt text.
   */
  | { kind: 'shot'; src: string; alt: string; caption?: string }
  /** A highlighted aside. `warn` is for "this costs money if you get it wrong". */
  | { kind: 'callout'; tone: 'tip' | 'note' | 'warn'; title?: string; text: string }
  /** A word list. This is how we teach vocabulary without a glossary page. */
  | { kind: 'defs'; intro?: string; items: { term: string; meaning: string }[] };

export interface HelpArticle {
  /** URL-safe id. Also the anchor a HelpTip points at. Never change one after release. */
  slug: string;
  /** Plain-language title. Prefer the question a beginner would actually ask. */
  title: string;
  /** One sentence, shown in search results and category lists. */
  summary: string;
  category: HelpCategoryId;
  /**
   * Words a beginner might search for that do NOT appear in the title — synonyms,
   * the jargon they arrived with, and common misspellings. This is what makes
   * search find "who owns the building" → the Clients article.
   */
  keywords: string[];
  /** Slugs of articles to read next. Validated at test time — a typo fails CI. */
  related?: string[];
  body: HelpBlock[];
}

/** Flatten an article's prose into one searchable string. */
export function articleText(a: HelpArticle): string {
  const parts: string[] = [a.title, a.summary, ...a.keywords];
  for (const b of a.body) {
    switch (b.kind) {
      case 'text':
      case 'heading':
        parts.push(b.text);
        break;
      case 'steps':
        if (b.intro) parts.push(b.intro);
        parts.push(...b.steps);
        break;
      case 'bullets':
        if (b.intro) parts.push(b.intro);
        parts.push(...b.items);
        break;
      case 'callout':
        if (b.title) parts.push(b.title);
        parts.push(b.text);
        break;
      case 'defs':
        if (b.intro) parts.push(b.intro);
        for (const d of b.items) parts.push(d.term, d.meaning);
        break;
      case 'shot':
        // The alt text is real prose about the screen, so it IS searchable.
        parts.push(b.alt);
        if (b.caption) parts.push(b.caption);
        break;
    }
  }
  return parts.join(' ');
}
